// frontend/src/components/booking/BookingForm.jsx
import React, { useState } from 'react';

const BookingForm = ({ flight, searchParams, onSubmit }) => {
  const [passengers, setPassengers] = useState(
    Array.from({ length: searchParams.passengers }, () => ({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      passport_number: '',
      nationality: ''
    }))
  );
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    special_requests: ''
  });
  const [errors, setErrors] = useState({});

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
    
    // Clear error for this field
    if (errors[`passenger_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`passenger_${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const handleContactChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate passengers
    passengers.forEach((passenger, index) => {
      if (!passenger.first_name.trim()) {
        newErrors[`passenger_${index}_first_name`] = 'First name is required';
      }
      if (!passenger.last_name.trim()) {
        newErrors[`passenger_${index}_last_name`] = 'Last name is required';
      }
      if (!passenger.date_of_birth) {
        newErrors[`passenger_${index}_date_of_birth`] = 'Date of birth is required';
      }
      if (!passenger.gender) {
        newErrors[`passenger_${index}_gender`] = 'Gender is required';
      }
    });

    // Validate contact info
    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contactInfo.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!contactInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    onSubmit({
      passengers,
      contact_email: contactInfo.email,
      contact_phone: contactInfo.phone,
      special_requests: contactInfo.special_requests,
      flight_id: flight.id
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="space-y-6">
      {/* Flight Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Flight Summary</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">{flight.airline_name} {flight.flight_number}</div>
            <div className="text-gray-600">{flight.origin_airport} → {flight.destination_airport}</div>
            <div className="text-sm text-gray-500">
              {formatTime(flight.departure_time)} - {formatTime(flight.arrival_time)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">${flight.base_price}</div>
            <div className="text-sm text-gray-500">per person</div>
          </div>
        </div>
      </div>

      {/* Passenger Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Passenger Information</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {passengers.map((passenger, index) => (
            <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
              <h3 className="text-lg font-medium mb-4">
                Passenger {index + 1}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={passenger.first_name}
                    onChange={(e) => handlePassengerChange(index, 'first_name', e.target.value)}
                    className={`input ${errors[`passenger_${index}_first_name`] ? 'error-input' : ''}`}
                  />
                  {errors[`passenger_${index}_first_name`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`passenger_${index}_first_name`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={passenger.last_name}
                    onChange={(e) => handlePassengerChange(index, 'last_name', e.target.value)}
                    className={`input ${errors[`passenger_${index}_last_name`] ? 'error-input' : ''}`}
                  />
                  {errors[`passenger_${index}_last_name`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`passenger_${index}_last_name`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={passenger.date_of_birth}
                    onChange={(e) => handlePassengerChange(index, 'date_of_birth', e.target.value)}
                    className={`input ${errors[`passenger_${index}_date_of_birth`] ? 'error-input' : ''}`}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {errors[`passenger_${index}_date_of_birth`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`passenger_${index}_date_of_birth`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender *
                  </label>
                  <select
                    value={passenger.gender}
                    onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                    className={`input ${errors[`passenger_${index}_gender`] ? 'error-input' : ''}`}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors[`passenger_${index}_gender`] && (
                    <p className="text-red-500 text-sm mt-1">{errors[`passenger_${index}_gender`]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Passport Number
                  </label>
                  <input
                    type="text"
                    value={passenger.passport_number}
                    onChange={(e) => handlePassengerChange(index, 'passport_number', e.target.value)}
                    className="input"
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nationality
                  </label>
                  <input
                    type="text"
                    value={passenger.nationality}
                    onChange={(e) => handlePassengerChange(index, 'nationality', e.target.value)}
                    className="input"
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Contact Information */}
          <div className="pt-6">
            <h3 className="text-lg font-medium mb-4">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  className={`input ${errors.email ? 'error-input' : ''}`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => handleContactChange('phone', e.target.value)}
                  className={`input ${errors.phone ? 'error-input' : ''}`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Special Requests
              </label>
              <textarea
                value={contactInfo.special_requests}
                onChange={(e) => handleContactChange('special_requests', e.target.value)}
                className="input"
                rows="3"
                placeholder="Dietary requirements, wheelchair assistance, etc."
              />
            </div>
          </div>

          {/* Total Amount */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>Total Amount:</span>
              <span className="text-blue-600">
                ${(flight.base_price * passengers.length).toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {passengers.length} passenger{passengers.length > 1 ? 's' : ''} × ${flight.base_price}
            </p>
          </div>

          <button
            type="submit"
            className="w-full btn btn-primary py-3 text-lg"
          >
            Continue to Payment
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;